/* eslint-disable perfectionist/sort-enums */
import z from 'zod'

/**
 * https://developers.bankid.com/ui-resources/user-messages
 */
export enum RfaCode {
  /** Start your BankID app. */
  RFA1 = 'RFA1',
  /** You don't seem to have the BankID app. Install the app and get a BankID. */
  RFA2 = 'RFA2',
  /** The action was cancelled. Please try again. */
  RFA3 = 'RFA3',
  /** An identification or signature is already in progress for your personal identity number. Please try again. */
  RFA4 = 'RFA4',
  /** Something went wrong. Please try again. */
  RFA5 = 'RFA5',
  /** The action was cancelled. */
  RFA6 = 'RFA6',
  /** The BankID app is not responding. Please check that it's started and that you have internet access. Try again. */
  RFA8 = 'RFA8',
  /** Enter your security code in the BankID app and select Identify or Sign. */
  RFA9 = 'RFA9',
  /** Trying to start your BankID app. */
  RFA13 = 'RFA13',
  /** Searching for BankID. Make sure you have a valid BankID on this computer. If you have a BankID on card, please insert the card into your card reader. */
  RFA15A = 'RFA15A',
  /** Searching for BankID. Make sure you have a valid BankID on this device. */
  RFA15B = 'RFA15B',
  /** Your BankID is blocked or too old. Please use another BankID or get a new one from your bank. */
  RFA16 = 'RFA16',
  /** You don't seem to have the BankID app/program. Please install it and get a BankID from your bank. */
  RFA17A = 'RFA17A',
  /** Failed to scan the QR code. Start the BankID app and scan the QR code. */
  RFA17B = 'RFA17B',
  /** Would you like to use BankID on this computer, or a Mobile BankID? */
  RFA19 = 'RFA19',
  /** Do you want to use BankID on this device or another device? */
  RFA20 = 'RFA20',
  /** An identification or signing is in progress. */
  RFA21 = 'RFA21',
  /** Something went wrong. Please try again. */
  RFA22 = 'RFA22',
  /** Take a photo of, and scan, your ID document with the BankID app. */
  RFA23 = 'RFA23',
}

export enum BankIdStatus {
  Pending = 'pending',
  Failed = 'failed',
  Complete = 'complete',
}

export enum PendingHintCode {
  OutstandingTransaction = 'outstandingTransaction',
  NoClient = 'noClient',
  Started = 'started',
  UserMrtd = 'userMrtd',
  UserCallConfirm = 'userCallConfirm',
  UserSign = 'userSign',
  Processing = 'processing',
}

export enum FailedHintCode {
  ExpiredTransaction = 'expiredTransaction',
  CertificateErr = 'certificateErr',
  UserCancel = 'userCancel',
  Cancelled = 'cancelled',
  StartFailed = 'startFailed',
  UserDeclinedCall = 'userDeclinedCall',
  NotSupportedByUserApp = 'notSupportedByUserApp',
  TransactionRiskBlocked = 'transactionRiskBlocked',
}

export type HintCode = PendingHintCode | FailedHintCode

export const AuthBodySchema = z.object({
  app: z
    .object({
      appIdentifier: z.string(),
      deviceIdentifier: z.string(),
      deviceModelName: z.string(),
      deviceOS: z.string(),
    })
    .optional(),
  endUserIp: z.string(),
  requirements: z
    .object({
      cardReader: z.enum(['class1', 'class2']).optional(),
      certificatePolicies: z.array(z.string()).optional(),
      mrtd: z.boolean().optional(),
      personalNumber: z.string().optional(),
      pinCode: z.string().optional(),
    })
    .optional(),
  returnRisk: z.boolean().optional(),
  returnUrl: z.url().optional(),
  userNonVisibleData: z.string().optional(),
  userVisibleData: z.string().optional(),
  userVisibleDataFormat: z.enum(['plaintext', 'simpleMarkdownV1']).optional(),
  web: z
    .object({
      deviceIdentifier: z.string().optional(),
      referringDomain: z.string().optional(),
      userAgent: z.string().optional(),
    })
    .optional(),
})

export const AuthResponseSchema = z.object({
  autoStartToken: z.string(),
  orderRef: z.string(),
  qrCode: z.string(),
})

export const OrderResponseSchema = z.object({
  autoStartToken: z.string(),
  orderRef: z.string(),
  qrStartSecret: z.string(),
  qrStartToken: z.string(),
})

export const CompletionDataSchema = z.object({
  bankIdIssueDate: z.string().optional(),
  device: z.object({
    ipAddress: z.string().optional(),
    uhi: z.string().optional(),
  }),
  ocspResponse: z.string().optional(),
  risk: z.enum(['low', 'medium', 'high']).optional(),
  signature: z.string().optional(),
  stepUp: z
    .object({
      mrtd: z.boolean().optional(),
    })
    .optional(),
  user: z.object({
    givenName: z.string().optional(),
    name: z.string().optional(),
    personalNumber: z.string().optional(),
    surname: z.string().optional(),
  }),
})

export const BankIdCompleteCollectResponseSchema = z.object({
  completionData: CompletionDataSchema,
  orderRef: z.string(),
  status: z.literal(BankIdStatus.Complete),
})

export const BankIdPendingCollectResponseSchema = z.object({
  hintCode: z.enum(PendingHintCode),
  orderRef: z.string(),
  status: z.literal(BankIdStatus.Pending),
})

export const BankIdFailedCollectResponseSchema = z.object({
  hintCode: z.enum(FailedHintCode),
  orderRef: z.string(),
  status: z.literal(BankIdStatus.Failed),
})

export const BankIdCollectResponseSchema = z.union([
  BankIdCompleteCollectResponseSchema,
  BankIdPendingCollectResponseSchema,
  BankIdFailedCollectResponseSchema,
])

export const CompleteCollectResponseSchema = BankIdCompleteCollectResponseSchema.pick({
  status: true,
}).extend({
  token: z.string(),
})

export const PendingCollectResponseSchema = BankIdPendingCollectResponseSchema.omit({
  orderRef: true,
}).extend({
  qrCode: z.string(),
  rfaCode: z.enum(RfaCode),
})

export const FailedCollectResponseSchema = BankIdFailedCollectResponseSchema.omit({
  orderRef: true,
}).extend({
  qrCode: z.string(),
  rfaCode: z.enum(RfaCode),
})

export const CollectResponseSchema = z.union([
  CompleteCollectResponseSchema,
  PendingCollectResponseSchema,
  FailedCollectResponseSchema,
])

export type AuthBody = z.infer<typeof AuthBodySchema>

export type AuthResponse = z.infer<typeof AuthResponseSchema>

export type OrderResponse = z.infer<typeof OrderResponseSchema>

export type BankIdCollectResponse = z.infer<typeof BankIdCollectResponseSchema>

export type BankidIdCompleteCollectResponse = z.infer<typeof BankIdCompleteCollectResponseSchema>

export type CompleteCollectResponse = z.infer<typeof CompleteCollectResponseSchema>

export type PendingCollectResponse = z.infer<typeof PendingCollectResponseSchema>

export type FailedCollectResponse = z.infer<typeof FailedCollectResponseSchema>

export type CollectResponse = z.infer<typeof CollectResponseSchema>
